// Roster (variable-data) orders: CSV template, parsing, and per-row validation.
// Header names are fixed by section 4C of the NMS developer guide.

export const ROSTER_HEADERS = [
  "Customer",
  "Garment_Size",
  "Garment_Color",
  "Player_Name",
  "Player_Number",
  "Quantity",
];

export const PLAYER_NAME_MAX = 15;

/** Minimal RFC 4180 CSV parser (quoted fields, escaped quotes, CRLF). */
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(field); rows.push(row); row = []; field = "";
    } else field += c;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows;
}

const csvCell = (v) => {
  const s = v == null ? "" : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function rosterTemplateCsv(example = true) {
  const lines = [ROSTER_HEADERS.join(",")];
  if (example) lines.push(["Jordan Smith", "M", "Black", "SMITH", "23", "1"].map(csvCell).join(","));
  return lines.join("\r\n") + "\r\n";
}

/**
 * Parse an uploaded roster CSV. Rejects the file unless the header row matches
 * ROSTER_HEADERS exactly; strips blank rows. Returns { rows } or { error }.
 */
export function parseRosterCsv(text) {
  const table = parseCsv(text.replace(/^﻿/, ""));
  if (!table.length) return { error: "The file is empty." };
  const header = table[0].map((h) => h.trim());
  const matches =
    header.length >= ROSTER_HEADERS.length &&
    ROSTER_HEADERS.every((h, i) => header[i] === h) &&
    header.slice(ROSTER_HEADERS.length).every((h) => h === "");
  if (!matches) {
    return {
      error: `Upload Rejected: The roster must use the template headers exactly: ${ROSTER_HEADERS.join(", ")}.`,
    };
  }
  const rows = table
    .slice(1)
    .filter((r) => r.some((cell) => cell.trim() !== ""))
    .map((r) => ({
      customer: (r[0] ?? "").trim(),
      size: (r[1] ?? "").trim(),
      color: (r[2] ?? "").trim(),
      name: (r[3] ?? "").trim(),
      number: (r[4] ?? "").trim(),
      qty: (r[5] ?? "").trim() === "" ? 1 : Number(r[5]),
    }));
  return { rows };
}

/** Resolve a size typed by a person ("adult m", "XXL") to a size id, or null. */
export function resolveSize(input, sizes) {
  const norm = String(input ?? "").trim().toUpperCase().replace(/\s+/g, " ");
  if (!norm) return null;
  const aliases = { XXL: "2XL", XXXL: "3XL", "2X": "2XL", "3X": "3XL", "4X": "4XL", "5X": "5XL" };
  const key = aliases[norm] ?? norm;
  const hit = sizes.find(
    (s) => s.id.toUpperCase() === key || s.label.toUpperCase() === key ||
      s.label.toUpperCase() === `ADULT ${key}`,
  );
  return hit ? hit.id : null;
}

/** Validate one roster row. Returns a list of { field, message }. */
export function validateRosterRow(row, { sizes, colors }) {
  const errs = [];
  if (!row.customer) errs.push({ field: "customer", message: "Recipient name is required." });
  if (!resolveSize(row.size, sizes)) errs.push({ field: "size", message: `Unknown size "${row.size}".` });
  if (colors?.length && !colors.some((c) => c.toLowerCase() === String(row.color ?? "").trim().toLowerCase())) {
    errs.push({ field: "color", message: `"${row.color}" is not an available color.` });
  }
  if (row.name && row.name.length > PLAYER_NAME_MAX) {
    errs.push({ field: "name", message: `Player name must be ${PLAYER_NAME_MAX} characters or fewer.` });
  }
  if (row.number !== "" && row.number != null && !/^\d{1,2}$/.test(String(row.number))) {
    errs.push({ field: "number", message: "Player number must be 0-99 (up to 2 digits)." });
  }
  if (!Number.isInteger(Number(row.qty)) || Number(row.qty) < 1) {
    errs.push({ field: "qty", message: "Quantity must be a whole number greater than 0." });
  }
  return errs;
}
